import { stopWords, wordToTopic } from '../data/dex';

const TIME_WINDOW_MS = 3 * 60 * 60 * 1000; // 3 hours
const SIMILARITY_THRESHOLD = 0.35; // 35% keyword overlap = duplicate

function extractKeywords(title) {
  return title
    .toLowerCase()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()"""'']/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 2 && !stopWords.has(word))
    .map(word => wordToTopic[word] || word);
}

function similarity(keywordsA, keywordsB) {
  if (!keywordsA.length || !keywordsB.length) return 0;
  const setA = new Set(keywordsA);
  const setB = new Set(keywordsB);
  const intersection = [...setA].filter(w => setB.has(w)).length;
  const union = new Set([...setA, ...setB]).size;
  return intersection / union;
}

function withinTimeWindow(dateA, dateB) {
  return Math.abs(new Date(dateA) - new Date(dateB)) <= TIME_WINDOW_MS;
}

// Returns groups of duplicate articles (each group = same story from multiple sources)
export function deduplicateArticles(articles) {
  // Each group: { versions: [article, ...], alsoOn: [{source, sourceName, link}] }
  const groups = [];

  for (const article of articles) {
    const keywordsA = extractKeywords(article.title);
    let matched = false;

    for (const group of groups) {
      const sameSourceInGroup = group.versions.some(v => v.source === article.source);
      for (const existing of group.versions) {
        const keywordsB = extractKeywords(existing.title);
        const score = similarity(keywordsA, keywordsB);
        const timeOk = withinTimeWindow(article.pubDate, existing.pubDate);
        // Cross-source: 35% threshold. Same-source: 70% threshold (very similar = duplicate)
        const threshold = sameSourceInGroup ? 0.7 : SIMILARITY_THRESHOLD;
        if (score >= threshold && timeOk) {
          if (!sameSourceInGroup) group.versions.push(article);
          matched = true;
          break;
        }
      }
      if (matched) break;
    }

    if (!matched) {
      groups.push({ versions: [article] });
    }
  }

  return groups;
}

// Pick the best version from a group based on enabled sources (priority order = sources array order)
export function resolveGroups(groups, enabledSources) {
  return groups.map(group => {
    // Pick newest version first
    const available = group.versions
      .filter(v => enabledSources.includes(v.source))
      .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
    if (available.length === 0) return null;

    // Pick first available (feed is already sorted by pubDate, so first = most relevant)
    const primary = available[0];
    const alsoOn = available.slice(1).map(v => ({
      source: v.source,
      sourceName: v.sourceName,
      link: v.link,
    }));

    return { article: primary, alsoOn };
  }).filter(Boolean);
}
